'use client';

import React from 'react';
import { 
    FilePlus, 
    UserCheck, 
    Zap, 
    ShieldCheck, 
    ArrowLeft,
    ChevronRight,
    CheckCircle2,
    Clock,
    FileSearch,
    MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { cn } from '@/doi-tac-module/lib/utils';
import Image from 'next/image';

interface AgencyQuickPostRoadmapContentProps {
    onBack: () => void;
    isPublic?: boolean;
}

const stepsData = [
    {
        id: 1,
        icon: UserCheck,
        color: "bg-[#19A6DF]/10 text-[#19A6DF]",
        image: "/img/PHONGVAN/phongvan01.jpg",
        title: {
            vi: "1. Đăng ký tài khoản",
            en: "1. Account Registration"
        },
        description: {
            vi: "Tạo tài khoản và xác thực danh tính để bắt đầu sử dụng các tính năng hỗ trợ Tokutei chuyên sâu.",
            en: "Create an account and verify your identity to start using in-depth Tokutei support features."
        },
        stageLabel: {
            vi: "Khởi tạo",
            en: "Initialization"
        }
    },
    {
        id: 2,
        icon: FilePlus,
        color: "bg-[#AFC536]/10 text-[#AFC536]",
        image: "/img/PHONGVAN/phongvan03.jpg",
        title: {
            vi: "2. Cập nhật thông tin",
            en: "2. Update Information"
        },
        description: {
            vi: "Điền đầy đủ thông tin về chứng chỉ Tokutei, JLPT và kinh nghiệm làm việc tại Nhật Bản để tối ưu hồ sơ.",
            en: "Fill in all information about Tokutei certificates, JLPT, and work experience in Japan to optimize your profile."
        },
        stageLabel: {
            vi: "Thực hiện",
            en: "Execution"
        }
    },
    {
        id: 3,
        icon: Zap,
        color: "bg-[#F2B92A]/10 text-[#F2B92A]",
        image: "/img/PHONGVAN/phongvan04.jpg",
        title: {
            vi: "3. Quay Video giới thiệu",
            en: "3. Record Intro Video"
        },
        description: {
            vi: "Tạo ấn tượng mạnh với nhà tuyển dụng thông qua Video Profile giới thiệu bản thân bằng tiếng Nhật chuyên nghiệp.",
            en: "Make a strong impression on employers through a professional Japanese self-introduction Video Profile."
        },
        stageLabel: {
            vi: "Tạo ấn tượng",
            en: "Impression"
        }
    },
    {
        id: 4,
        icon: MessageSquare,
        color: "bg-[#0D8DC8]/10 text-[#0D8DC8]",
        image: "/img/PHONGVAN/phongvan05.jpg",
        title: {
            vi: "4. Sẵn sàng kết nối",
            en: "4. Ready to Connect"
        },
        description: {
            vi: "Hồ sơ của bạn đã sẵn sàng để được gợi ý cho hàng ngàn doanh nghiệp Nhật Bản đang tìm kiếm nhân sự Tokutei.",
            en: "Your profile is ready to be suggested to thousands of Japanese businesses looking for Tokutei personnel."
        },
        stageLabel: {
            vi: "Kết nối",
            en: "Connection"
        }
    }
];

export default function AgencyQuickPostRoadmapContent({ onBack, isPublic = false }: AgencyQuickPostRoadmapContentProps) {
    const { language } = useLanguage();

    return (
        <div className="flex flex-col w-full min-h-screen bg-slate-50/50">
            {/* Header / Nav */}
            <div className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <Button variant="ghost" onClick={onBack} className="gap-2 font-bold text-slate-600 hover:text-[#19A6DF] transition-colors">
                        <ArrowLeft className="h-5 w-5" />
                        {language === 'vi' ? "Quay lại" : "Back"}
                    </Button>
                    <div className="font-bold text-slate-800 hidden md:block">
                        {language === 'vi' ? "Quy trình Hoàn thiện hồ sơ" : "Profile Completion Process"}
                    </div>
                    <div className="w-10 md:hidden" /> {/* Spacer */}
                </div>
            </div>

            {/* Hero Section */}
            <section className="relative w-full py-20 bg-[#AFC536] bg-gradient-to-br from-[#AFC536] to-[#8FA32D] text-white overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,rgba(255,255,255,0),rgba(255,255,255,0.5))]" />
                <div className="container relative mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1 text-sm font-bold backdrop-blur-md border border-white/30 mb-6">
                        <Zap className="h-4 w-4" />
                        {language === 'vi' ? "Nổi bật hơn với hồ sơ năng lực" : "Stand out with your competency profile"}
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold font-headline mb-6 leading-snug md:leading-tight">
                        {language === 'vi' ? "Hoàn thiện hồ sơ" : "Complete your profile"} <br /> 
                        <span className="text-[#F2B92A]">{language === 'vi' ? "Chỉ trong 5 phút" : "In just 5 minutes"}</span>
                    </h1>
                    <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10 font-medium">
                        {language === 'vi' 
                            ? "Quy trình chuẩn hóa hồ sơ giúp ứng viên Tokutei Việt Nam tại Nhật Bản nổi bật hơn và tăng 90% cơ hội nhận được lời mời phỏng vấn." 
                            : "Standardized profile process helps Vietnamese Tokutei candidates in Japan stand out and increases the chance of receiving an interview invite by 90%."}
                    </p>
                    <div className="flex justify-center gap-4">
                        <Button size="lg" className="bg-white text-[#AFC536] hover:bg-slate-100 font-bold h-12 px-8 rounded-xl shadow-xl transition-all hover:scale-105">
                            {language === 'vi' ? "Bắt đầu ngay" : "Start now"}
                        </Button>
                    </div>
                </div>
                
                {/* Abstract shapes */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" />
            </section>

            {/* Quick Benefits */}
            <section className="container mx-auto px-4 py-16 -mt-10 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="border-none shadow-xl rounded-3xl bg-white overflow-hidden group">
                        <CardContent className="p-8">
                            <div className="w-14 h-14 rounded-2xl bg-orange-50 text-[#F2B92A] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <Clock className="h-7 w-7" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">{language === 'vi' ? "Tiết kiệm thời gian" : "Time-saving"}</h3>
                            <p className="text-slate-500">{language === 'vi' ? "Tạo hồ sơ chuyên nghiệp nhanh chóng với các mẫu chuẩn Nhật Bản." : "Create professional profiles quickly with Japanese standard templates."}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-none shadow-xl rounded-3xl bg-white overflow-hidden group">
                        <CardContent className="p-8">
                            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-[#19A6DF] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <FileSearch className="h-7 w-7" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">{language === 'vi' ? "Hồ sơ chất lượng" : "Quality Profile"}</h3>
                            <p className="text-slate-500">{language === 'vi' ? "Làm nổi bật kỹ năng và chứng chỉ Tokutei để thu hút nhà tuyển dụng." : "Highlight Tokutei skills and certificates to attract employers."}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-none shadow-xl rounded-3xl bg-white overflow-hidden group">
                        <CardContent className="p-8">
                            <div className="w-14 h-14 rounded-2xl bg-green-50 text-[#AFC536] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <ShieldCheck className="h-7 w-7" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">{language === 'vi' ? "An toàn & Bảo mật" : "Safe & Secure"}</h3>
                            <p className="text-slate-500">{language === 'vi' ? "Thông tin cá nhân được bảo mật và chỉ chia sẻ với đối tác uy tín." : "Personal information is secure and only shared with reputable partners."}</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* Steps Section */}
            <section className="container mx-auto px-4 py-20">
                <div className="max-w-3xl mx-auto text-center mb-20">
                    <h2 className="text-3xl md:text-4xl font-bold font-headline mb-6 text-slate-900">
                        {language === 'vi' ? "Lộ trình 4 bước bứt phá" : "4-step breakthrough roadmap"}
                    </h2>
                    <p className="text-lg text-slate-500">
                        {language === 'vi' 
                            ? "Chúng tôi hỗ trợ bạn chuẩn bị mọi thứ tốt nhất để sẵn sàng kết nối với công việc mơ ước tại Nhật Bản." 
                            : "We support you in preparing everything best to be ready to connect with your dream job in Japan."}
                    </p>
                </div>

                <div className="space-y-24">
                    {stepsData.map((step, index) => (
                        <div key={step.id} className={cn(
                            "flex flex-col lg:flex-row items-center gap-12 lg:gap-24",
                            index % 2 !== 0 && "lg:flex-row-reverse"
                        )}>
                            <div className="lg:w-1/2 w-full">
                                <div className="relative group">
                                    <div className={cn("absolute -inset-4 rounded-[2rem] blur-2xl opacity-20 group-hover:opacity-40 transition-opacity", step.color)} />
                                    <div className="relative aspect-[16/10] rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white bg-white">
                                        <Image
                                            src={step.image}
                                            alt={step.title[language as keyof typeof step.title]}
                                            fill
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            priority={index === 0}
                                            className="object-cover transition-transform duration-700 group-hover:scale-105 will-change-transform"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="lg:w-1/2">
                                <div className={cn("inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl text-sm font-bold tracking-wider", step.color)}>
                                    <step.icon className="h-5 w-5" />
                                    {step.stageLabel[language as keyof typeof step.stageLabel]}
                                </div>
                                <h3 className="text-3xl font-bold mb-6 text-slate-900 leading-tight">
                                    {step.title[language as keyof typeof step.title]}
                                </h3>
                                <p className="text-xl text-slate-600 leading-relaxed mb-10">
                                    {step.description[language as keyof typeof step.description]}
                                </p>
                                <Button className="bg-[#AFC536] hover:bg-[#AFC536]/90 text-white font-bold h-12 px-8 rounded-xl shadow-lg group">
                                    {language === 'vi' ? "Thực hiện ngay" : "Perform now"}
                                    <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Final CTA */}
            <section className="bg-slate-900 py-24 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-[#AFC536]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="container relative mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold font-headline mb-8">
                        {language === 'vi' ? "Bắt đầu bứt phá cùng HelloJob?" : "Ready to break through with HelloJob?"}
                    </h2>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-12">
                        {language === 'vi' 
                            ? "Hãy để nhà tuyển dụng tìm thấy bạn. Một hồ sơ đầy đủ giúp tăng 90% cơ hội nhận được lời mời phỏng vấn." 
                            : "Let employers find you. A complete profile increases your chances of receiving an interview invite by 90%."}
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Button size="lg" className="bg-[#AFC536] hover:bg-[#AFC536]/90 text-white font-bold h-14 px-12 rounded-2xl text-lg shadow-2xl transition-all hover:scale-105">
                            {language === 'vi' ? "Hoàn thiện hồ sơ ngay" : "Complete Profile Now"}
                        </Button>
                    </div>
                    <div className="mt-16 flex items-center justify-center gap-8 opacity-50 grayscale">
                        <CheckCircle2 className="h-10 w-10" />
                        <span className="text-sm font-bold tracking-widest uppercase">Verified by HelloJob Japan</span>
                    </div>
                </div>
            </section>
        </div>
    );
}

